"use client";

import { ArrowRight, FileCheck, Gauge, ShieldCheck, Users } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Sectors() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const cards = [{
    icon: FileCheck,
    title: "Automotive Engineering",
    body: "Precision-driven sales representation for Tier 1 and Tier 2 automotive suppliers, focusing on high-volume production and technical compliance.",
    proof: "Proven track record in OEM supply chains"
  }, {
    icon: Gauge,
    title: "Metal Fabrication",
    body: "Connecting advanced metal processing capabilities with industrial buyers who demand strict tolerances and material integrity.",
    proof: "Expertise in welding equipment"
  }, {
    icon: ShieldCheck,
    title: "Safety Systems",
    body: "Representing manufacturers of critical safety infrastructure, ensuring compliance with rigorous industrial standards and regulatory mandates.",
    proof: "Deep knowledge of safety protocols"
  }, {
    icon: Users,
    title: "Shipbuilding & Marine",
    body: "Strategic sales support for marine-grade components and systems, navigating the unique procurement cycles of the shipbuilding industry.",
    proof: "15+ yrs of maritime sales experience"
  }];
  return <section data-section-id="15851" id="core-sectors" className="bg-background text-foreground py-24 md:py-32 font-default">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 md:px-14">
        {}
        <div className="h-px w-full bg-border" />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10">
          {}
          <div className="lg:col-span-4">
            <div ref={leftRef} className="lg:sticky lg:top-24 transition-all duration-[520ms] ease-out" style={{
            transform: leftVisible ? "translateY(0)" : "translateY(16px)",
            opacity: leftVisible ? 1 : 0
          }}>
              <p className="text-[13px] font-medium uppercase text-muted-foreground" style={{
              letterSpacing: "0.14em"
            }}>
                {"Our Expertise"}
              </p>

              <h2 className="mt-6 font-serif text-foreground" style={{
              fontSize: "clamp(2rem, 3.6vw, 3.25rem)",
              fontWeight: 400,
              letterSpacing: "-0.018em",
              lineHeight: 1.1
            }}>
                {"Engineered for the "}
                <span className="relative italic text-primary">
                  {"demands"}
                  <span aria-hidden="true" className="absolute left-0 right-0 bg-accent" style={{
                  height: "1px",
                  bottom: "-4px"
                }} />
                </span>
                {" of heavy industry."}
              </h2>

              <p className="mt-8 text-foreground/85" style={{
              fontSize: "clamp(1.0625rem, 1.2vw, 1.1875rem)",
              lineHeight: 1.6,
              maxWidth: "32ch"
            }}>
                MetPro Partners operates at the intersection of technical capability and market reach. We represent manufacturers who define the standard, ensuring your products reach the decision-makers who need them most.
              </p>

              <Link to="/ServicesPage" className="group mt-10 inline-flex items-center gap-2 text-foreground">
                <span className="relative pb-1">
                  {"View our capabilities"}
                  <span aria-hidden="true" className="absolute left-0 bottom-0 h-px bg-border transition-all duration-[240ms] ease-out group-hover:w-full group-hover:bg-accent" style={{
                  width: "60%"
                }} />
                </span>
                <ArrowRight className="size-4 transition-transform duration-[240ms] ease-out group-hover:translate-x-1" strokeWidth={1.25} />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {cards.map((card, i) => {
              const Icon = card.icon;
              return <AdvantageCard key={card.title} data-index={i} index={i} icon={Icon} title={card.title} body={card.body} proof={card.proof} />;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function AdvantageCard({
  index,
  icon: Icon,
  title,
  body,
  proof,
  ...rest
}: {
  index: number;
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  body: string;
  proof: string;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} {...rest} className="group relative bg-card p-8 border border-border transition-all duration-[280ms] ease-out hover:shadow-lg" style={{
    borderRadius: "0px",
    transform: isVisible ? "translateY(0)" : "translateY(16px)",
    opacity: isVisible ? 1 : 0,
    transitionDelay: `${index * 80}ms`,
    transitionProperty: "transform, opacity, box-shadow",
    transitionDuration: "520ms, 520ms, 280ms"
  }}>
      <Icon className="size-6 text-foreground" strokeWidth={1.25} />

      <h3 className="relative mt-6 inline-block text-foreground" style={{
      fontFamily: "var(--font-sans)",
      fontSize: "clamp(1.25rem, 1.6vw, 1.5rem)",
      fontWeight: 500,
      letterSpacing: "-0.01em",
      lineHeight: 1.25
    }}>
        {title}
        <span aria-hidden="true" className="absolute left-0 bg-accent transition-all duration-[220ms] ease-out group-hover:w-full" style={{
        height: "1px",
        bottom: "-4px",
        width: "0%"
      }} />
      </h3>

      <p className="mt-4 text-foreground/85" style={{
      fontSize: "clamp(0.9375rem, 1vw, 1rem)",
      lineHeight: 1.62
    }}>
        {body}
      </p>

      <p className="mt-6 text-muted-foreground" style={{
      fontFamily: "var(--font-mono)",
      fontSize: "0.8125rem",
      letterSpacing: "0.02em",
      lineHeight: 1.3
    }}>
        {proof}
      </p>
    </div>;
}